import React, { useEffect } from 'react';
import { outcomeTotals } from '@root/lib';
import { appSelector, type AppSliceState } from '@root/store/slices/appSlice';
import { balanceSelector } from '@root/store/slices/balanceSlice';
import { journeySelector } from '@root/store/slices/journeySlice';
import { roundSelector } from '@root/store/slices/roundSlice';
import { useActions, useTypedSelector } from '@root/store/store';
import { useTranslation } from 'react-i18next';
import RunOutcomeDialog from '../RunOutcomeDialog';

// How a run ended in play. Given up and expired show on the funding card.
const RunResultModal: React.FC = () => {
  const { t } = useTranslation();
  const { runResultOpen, runResultOutcome, runResultTotals, runResultRoundId } =
    useTypedSelector<AppSliceState>(appSelector);
  const { round } = useTypedSelector(roundSelector);
  const { balance } = useTypedSelector(balanceSelector);
  const { colour } = useTypedSelector(journeySelector);
  const { closeRunResult, setMenuPopupOpen, setMyBetsHistoryModalOpen } =
    useActions();

  // Only a roll means the player has moved on. The round seeded straight
  // after the result does not.
  useEffect(() => {
    if (!runResultOpen || !runResultRoundId) return;
    const movedOn =
      round?.id !== runResultRoundId && !!round?.state?.currentRoll;
    if (movedOn) closeRunResult();
  }, [
    runResultOpen,
    round?.id,
    round?.state?.currentRoll,
    runResultRoundId,
    closeRunResult,
  ]);

  if (!runResultOpen || !runResultOutcome) return null;

  const won = runResultOutcome === 'WON';
  const totals = runResultTotals ?? outcomeTotals(undefined, won);

  return (
    <RunOutcomeDialog
      title={won ? t('You reached the finish!') : t('You were eliminated')}
      emblem={won ? `Crown_${colour}` : 'Skull'}
      headline={
        won
          ? t('6X FINISH · +{{amount}}', {
              amount: `${balance.currencySymbol}${totals.finish}`,
            })
          : t('LOSS')
      }
      won={won}
      totals={totals}
      note={
        won
          ? t('Your winnings have been credited.')
          : t(
              'The skull ends your round. Previously credited rewards remain yours.'
            )
      }
      symbol={balance.currencySymbol}
      onNewRound={() => closeRunResult()}
      onViewDetails={() => setMyBetsHistoryModalOpen(true)}
      onMenu={() => setMenuPopupOpen(true)}
    />
  );
};

export default RunResultModal;
